import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

const C = {
  navy: '#0e225d', navyMid: '#1a3370', gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.18)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#E5E7EB', bg: '#F5F7FA', muted: '#9ca3af', sub: '#6b7280',
};

export interface EditablePayment {
  payment_id: string;
  amount_paid: number;
  discount_amount: number;
  discount_reason: string | null;
  payment_date: string | null;
  status: string;
  remarks: string | null;
  receipt_number: string | null;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '0.85rem', color: C.navy, background: C.white, boxSizing: 'border-box', outline: 'none',
};
const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '6px', fontSize: '0.68rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: C.sub,
};

const dialogPt = {
  root:    { style: { borderRadius: '16px', overflow: 'hidden' } },
  header:  { style: { display: 'none' } },
  content: { style: { padding: '22px' } },
};

const ModalHeader: React.FC<{ icon: string; title: string; sub?: string; onHide: () => void }> = ({ icon, title, sub, onHide }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`pi ${icon}`} style={{ color: C.gold, fontSize: '0.9rem' }} />
      </div>
      <div>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: C.navy }}>{title}</h3>
        {sub && <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted, fontFamily: 'monospace' }}>{sub}</p>}
      </div>
    </div>
    <button onClick={onHide} style={{ background: C.bg, border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', color: C.sub }}>
      <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Edit modal
// ─────────────────────────────────────────────────────────────────────────────
interface EditProps {
  visible: boolean;
  payment: EditablePayment | null;
  onHide: () => void;
  onSaved: () => void;
}

export const PaymentEditModal: React.FC<EditProps> = ({ visible, payment, onHide, onSaved }) => {
  const [amount, setAmount]     = useState('');
  const [discount, setDiscount] = useState('');
  const [reason, setReason]     = useState('');
  const [date, setDate]         = useState('');
  const [status, setStatus]     = useState('completed');
  const [remarks, setRemarks]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !payment) return;
    setAmount(String(payment.amount_paid ?? ''));
    setDiscount(String(payment.discount_amount ?? 0));
    setReason(payment.discount_reason || '');
    setDate(payment.payment_date ? payment.payment_date.slice(0, 10) : '');
    setStatus(payment.status || 'completed');
    setRemarks(payment.remarks || '');
    setError(null);
  }, [visible, payment]);

  if (!payment) return null;

  // Build a payload of changed fields only
  const buildPayload = (): Record<string, any> => {
    const p: Record<string, any> = {};
    if (Number(amount) !== Number(payment.amount_paid)) p.amount_paid = Number(amount);
    if (Number(discount || 0) !== Number(payment.discount_amount || 0)) p.discount_amount = Number(discount || 0);
    if (reason.trim() !== (payment.discount_reason || '')) p.discount_reason = reason.trim();
    if (date && date !== (payment.payment_date ? payment.payment_date.slice(0, 10) : '')) p.payment_date = date;
    if (status !== payment.status) p.status = status;
    if (remarks.trim() !== (payment.remarks || '')) p.remarks = remarks.trim();
    return p;
  };

  const handleSave = async () => {
    setError(null);
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setError('Amount must be greater than 0'); return; }
    if (discount !== '' && (isNaN(Number(discount)) || Number(discount) < 0)) { setError('Discount cannot be negative'); return; }

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { setError('No changes to save'); return; }

    try {
      setSaving(true);
      await apiClient.put(`/v1/fees/payment/${payment.payment_id}`, payload);
      onSaved();
      onHide();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update payment');
    } finally { setSaving(false); }
  };

  return (
    <Dialog visible={visible} onHide={onHide} style={{ width: '92vw', maxWidth: '520px' }} draggable={false} dismissableMask pt={dialogPt}>
      <ModalHeader icon="pi-pencil" title="Edit Payment" sub={payment.receipt_number || undefined} onHide={onHide} />

      {error && <Message severity="error" text={error} style={{ marginBottom: '14px', width: '100%', justifyContent: 'flex-start' }} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <div>
          <label style={labelStyle}>Amount Paid (₹)</label>
          <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Discount (₹)</label>
          <input type="number" min="0" value={discount} onChange={e => setDiscount(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Discount Reason</label>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Payment Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Remarks</label>
          <textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>

      <p style={{ margin: '14px 0 0', fontSize: '0.72rem', color: C.muted }}>
        <i className="pi pi-info-circle" style={{ marginRight: '5px', fontSize: '0.7rem' }} />
        Every edit is logged with your name. The last 5 changes are kept.
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
        <button onClick={onHide} disabled={saving} style={{ padding: '10px 20px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', color: C.sub, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.82rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// History modal (last 5 edits)
// ─────────────────────────────────────────────────────────────────────────────
interface HistoryEntry {
  edited_by: string | null;
  edited_by_name: string | null;
  edited_by_role: string | null;
  edited_at: string | null;
  changes: Record<string, { from: any; to: any }>;
}

interface HistoryProps {
  visible: boolean;
  paymentId: string | null;
  receiptNumber?: string | null;
  onHide: () => void;
}

const FIELD_LABEL: Record<string, string> = {
  amount_paid: 'Amount Paid', discount_amount: 'Discount', discount_reason: 'Discount Reason',
  payment_date: 'Payment Date', status: 'Status', remarks: 'Remarks',
};
const MONEY_FIELDS = ['amount_paid', 'discount_amount'];

const fmtValue = (field: string, v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (MONEY_FIELDS.includes(field)) return `₹${Number(v).toLocaleString('en-IN')}`;
  if (field === 'payment_date') {
    const d = new Date(String(v).replace(' ', 'T') + 'Z');
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return String(v);
};

const fmtWhen = (s: string | null) => {
  if (!s) return '-';
  const d = new Date(s.replace(' ', 'T') + 'Z'); // backend returns UTC
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const PaymentHistoryLogModal: React.FC<HistoryProps> = ({ visible, paymentId, receiptNumber, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    if (!visible || !paymentId) return;
    (async () => {
      try {
        setLoading(true); setError(null);
        const res = await apiClient.get(`/v1/fees/payment/${paymentId}/history`);
        setHistory(res.data?.data?.history || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load edit history');
      } finally { setLoading(false); }
    })();
  }, [visible, paymentId]);

  return (
    <Dialog visible={visible} onHide={onHide} style={{ width: '92vw', maxWidth: '560px' }} draggable={false} dismissableMask pt={dialogPt}>
      <ModalHeader icon="pi-history" title="Edit History" sub={receiptNumber || undefined} onHide={onHide} />

      {error && <Message severity="error" text={error} style={{ width: '100%', justifyContent: 'flex-start' }} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="3" />
        </div>
      ) : !error && history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '36px 10px', background: C.bg, borderRadius: '12px' }}>
          <i className="pi pi-inbox" style={{ fontSize: '1.6rem', color: C.navy, opacity: 0.25 }} />
          <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: C.sub }}>This payment has not been edited.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '60vh', overflowY: 'auto' }}>
          <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted }}>Showing the last {history.length} change{history.length > 1 ? 's' : ''}, newest first.</p>
          {history.map((h, i) => (
            <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px', padding: '10px 14px', background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.navy }}>
                  <i className="pi pi-user" style={{ fontSize: '0.75rem', marginRight: '6px', color: C.gold }} />
                  {h.edited_by_name || 'Unknown user'}
                  {h.edited_by_role && (
                    <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '20px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, color: '#92580a', fontSize: '0.62rem', fontWeight: 700, textTransform: 'capitalize' }}>
                      {h.edited_by_role.replace('_', ' ')}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: '0.72rem', color: C.muted }}>{fmtWhen(h.edited_at)}</span>
              </div>
              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(h.changes || {}).map(([field, c]) => (
                  <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                    <span style={{ minWidth: '110px', fontWeight: 700, color: C.sub, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      {FIELD_LABEL[field] || field}
                    </span>
                    <span style={{ padding: '2px 8px', borderRadius: '6px', background: '#fef2f2', color: '#dc2626', textDecoration: 'line-through', textTransform: field === 'status' ? 'capitalize' : 'none' }}>
                      {fmtValue(field, c.from)}
                    </span>
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.7rem', color: C.muted }} />
                    <span style={{ padding: '2px 8px', borderRadius: '6px', background: '#f0fdf4', color: '#15803d', fontWeight: 600, textTransform: field === 'status' ? 'capitalize' : 'none' }}>
                      {fmtValue(field, c.to)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
};
